import useSWR, { mutate as globalMutate, useSWRConfig } from 'swr';
import {
  // ── Fetch helpers ──────────────────────────────────────────
  getLeadFollowUps,              // dev mock — per-lead, used in dev env
  getFollowUps,                  // legacy / generic GET list
  getFollowUpsList,              // POST GetCalenderByFilters — Activities List view
  getFollowUpsByLeadId,          // real API — per-lead (used in CalendarTab)
  getFollowUpsByDateRange,       // calendar date-range (FollowUpsCalendar)
  getFollowUpsByMonthAndYear,    // legacy month/year calendar view
  getUpcomingFollowUps,          // upcoming N days (dashboard widget)
  getFollowUpEvents,             // outcome / event-type dropdown options
  getActivityTypes,              // new API for activity types
  getActivityBaseTypes,          // fetch base channel types
  getActivityById,               // fetch activity by ID
  getFollowUpStats,              // aggregate statistics (dashboard)
  getUsersForAssignment,         // assignable users dropdown
  // ── Mutation helpers ───────────────────────────────────────
  createFollowUp,
  createMultipleFollowUp,
  updateFollowUp,
  updateFollowUpStatus,
  rescheduleFollowUp,
  updateMultipleFollowUps,
  bulkUpdateFollowUpStatus,
  deleteFollowUp,
} from '../services/followUpService';
import { getLocalISODate } from '../utils/dateUtils';
import { invalidateNotesCache } from './useNotes';
import { invalidateTimelineCache } from './useTimeline';
import { invalidateMyScheduledItemsCache } from './useMySchedule';

// ─────────────────────────────────────────────
//  Cache-key helpers
// ─────────────────────────────────────────────

/**
 * Cache key for follow-ups belonging to a single lead.
 * @param {string|null} leadId
 */
export function getLeadFollowUpsCacheKey(leadId) {
  if (!leadId) return null;
  return ['followUps', 'byLead', String(leadId)];
}

/**
 * Cache key for calendar / date-range follow-ups.
 * @param {string} fromDate  – YYYY-MM-DD
 * @param {string} toDate    – YYYY-MM-DD
 * @param {number} isApplication  – 0 = All, 1 = Leads, 2 = Applicants
 */
export function getCalendarFollowUpsCacheKey(fromDate, toDate, isApplication = 0) {
  if (!fromDate || !toDate) return null;
  return ['followUps', 'calendar', fromDate, toDate, isApplication];
}

/** Cache key for the static follow-up event types list. */
export const FOLLOW_UP_EVENTS_KEY = ['followUps', 'events'];

/** Cache key for activity types list. */
export const ACTIVITY_TYPES_KEY = ['followUps', 'activityTypes'];

/** Cache key for activity base types list. */
export const ACTIVITY_BASE_TYPES_KEY = ['followUps', 'activityBaseTypes'];

/** Cache key for the assignable users list. */
export const ASSIGNMENT_USERS_KEY = ['followUps', 'assignmentUsers'];

// ─────────────────────────────────────────────
//  Cache-invalidation helpers
// ─────────────────────────────────────────────

/** Invalidate ALL follow-up related cache entries. */
export function invalidateAllFollowUpsCache() {
  return globalMutate(
    (key) => Array.isArray(key) && key[0] === 'followUps',
    undefined,
    { revalidate: true }
  );
}

/** Invalidate only the per-lead follow-up cache entries. */
export function invalidateLeadFollowUpsCache(leadId) {
  if (leadId) {
    return globalMutate(getLeadFollowUpsCacheKey(leadId), undefined, { revalidate: true });
  }
  return globalMutate(
    (key) => Array.isArray(key) && key[0] === 'followUps' && key[1] === 'byLead',
    undefined,
    { revalidate: true }
  );
}

/** Invalidate only the calendar / date-range cache entries. */
export function invalidateCalendarFollowUpsCache() {
  return globalMutate(
    (key) => Array.isArray(key) && key[0] === 'followUps' && key[1] === 'calendar',
    undefined,
    { revalidate: true }
  );
}

// ─────────────────────────────────────────────
//  Hook: useLeadFollowUps
//  Fetches all follow-ups for a specific lead.
// ─────────────────────────────────────────────

/**
 * Fetch and manage follow-ups for a single lead.
 *
 * @param {string|null} leadId
 * @returns {{
 *   followUps: Array,
 *   totalRecords: number,
 *   isLoading: boolean,
 *   error: any,
 *   mutate: function,
 *   refresh: function,
 *   scheduleFollowUp: function,
 * }}
 */
export function useLeadFollowUps(leadId) {
  const key = getLeadFollowUpsCacheKey(leadId);

  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getFollowUpsByLeadId(leadId);

      if (result?.success) {
        return {
          followUps: result.data || [],
          totalRecords: result.totalRecords || (result.data?.length ?? 0),
        };
      }

      // Treat "not found" (404) as an empty list rather than an error
      if (result?.statusCode === 404 || result?.error?.includes?.('not found')) {
        return { followUps: [], totalRecords: 0 };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch follow-ups');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      errorRetryCount: 3,
      errorRetryInterval: 5000,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  const refresh = () => invalidateLeadFollowUpsCache(leadId);

  /**
   * Create a new follow-up then immediately refresh the lead's list.
   * @param {Object} followUpData
   * @returns {Promise<Object>} API result
   */
  const scheduleFollowUp = async (followUpData) => {
    const result = await createFollowUp(followUpData);
    if (result?.success) {
      await mutate(); // refresh cache
    }
    return result;
  };

  return {
    followUps: data?.followUps || [],
    totalRecords: data?.totalRecords ?? 0,
    isLoading,
    error,
    mutate,
    refresh,
    scheduleFollowUp,
  };
}

// ─────────────────────────────────────────────
//  Hook: useCalendarFollowUps
//  Fetches calendar data for a date range.
// ─────────────────────────────────────────────

/**
 * Fetch follow-up calendar data for a date range.
 *
 * @param {string|Date|null} fromDate  – start of range
 * @param {string|Date|null} toDate    – end of range
 * @param {number} isApplication       – 0=All, 1=Leads, 2=Applicants
 * @returns {{
 *   calendarData: Object,          // keyed by YYYY-MM-DD
 *   rawData: Array,
 *   totalRecords: number,
 *   isLoading: boolean,
 *   error: any,
 *   mutate: function,
 *   refresh: function,
 * }}
 */
export function useCalendarFollowUps(fromDate, toDate, isApplication = 0) {
  // Normalise to YYYY-MM-DD strings
  const fromStr = fromDate
    ? (fromDate instanceof Date ? getLocalISODate(fromDate) : fromDate)
    : null;
  const toStr = toDate
    ? (toDate instanceof Date ? getLocalISODate(toDate) : toDate)
    : null;

  const key = getCalendarFollowUpsCacheKey(fromStr, toStr, isApplication);

  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getFollowUpsByDateRange(fromStr, toStr, isApplication);

      if (result?.success) {
        return {
          calendarData: result.data || {},
          rawData: result.rawData || [],
          totalRecords: result.totalRecords || 0,
        };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch calendar follow-ups');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      errorRetryCount: 2,
      errorRetryInterval: 5000,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  const refresh = () => invalidateCalendarFollowUpsCache();

  return {
    calendarData: data?.calendarData || {},
    rawData: data?.rawData || [],
    totalRecords: data?.totalRecords ?? 0,
    isLoading,
    error,
    mutate,
    refresh,
  };
}

// ─────────────────────────────────────────────
//  Hook: useFollowUpEvents
//  Fetches the list of event/outcome types.
// ─────────────────────────────────────────────

/**
 * Fetch the static list of follow-up event types (outcomes).
 *
 * @returns {{
 *   events: Array,
 *   isLoading: boolean,
 *   error: any,
 *   mutate: function,
 * }}
 */
export function useFollowUpEvents() {
  const { data, error, isLoading, mutate } = useSWR(
    FOLLOW_UP_EVENTS_KEY,
    async () => {
      const result = await getFollowUpEvents();

      if (result?.success) {
        return { events: result.data || [] };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch follow-up events');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,   // static list — no need to refetch on reconnect
      dedupingInterval: 60_000,       // cache for 1 min
      errorRetryCount: 2,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  return {
    events: data?.events || [],
    isLoading,
    error,
    mutate,
  };
}

// ─────────────────────────────────────────────
//  Hook: useActivityTypes
//  Fetches the list of activity types from new API.
// ─────────────────────────────────────────────

/**
 * Fetch the list of activity types (channels).
 *
 * @returns {{
 *   types: Array,
 *   isLoading: boolean,
 *   error: any,
 *   mutate: function,
 * }}
 */
export function useActivityTypes() {
  const { data, error, isLoading, mutate } = useSWR(
    ACTIVITY_TYPES_KEY,
    async () => {
      const result = await getActivityTypes();

      if (result?.success) {
        return { types: result.data || [] };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch activity types');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,   // static list
      dedupingInterval: 60_000,       // cache for 1 min
      errorRetryCount: 2,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  const refresh = () => globalMutate(ACTIVITY_TYPES_KEY, undefined, { revalidate: true });

  return {
    types: data?.types || [],
    isLoading,
    error,
    mutate,
    refresh,
  };
}

/**
 * Hook to fetch all activity base types (channels).
 * 
 * @returns {{
 *   baseTypes: Array,
 *   isLoading: boolean,
 *   error: any,
 *   mutate: function,
 * }}
 */
export function useActivityBaseTypes() {
  const { data, error, isLoading, mutate } = useSWR(
    ACTIVITY_BASE_TYPES_KEY,
    async () => {
      const result = await getActivityBaseTypes();

      if (result?.success) {
        return { baseTypes: result.data || [] };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch activity base types');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,   // static list
      dedupingInterval: 300_000,      // cache for 5 min
      errorRetryCount: 2,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  return {
    baseTypes: data?.baseTypes || [],
    isLoading,
    error,
    mutate,
  };
}


// ─────────────────────────────────────────────
//  Hook: useAssignmentUsers
//  Fetches the list of users available for assignment.
// ─────────────────────────────────────────────

/**
 * Fetch the list of users that can be assigned to follow-ups.
 *
 * @returns {{
 *   users: Array,
 *   isLoading: boolean,
 *   error: any,
 *   mutate: function,
 * }}
 */
export function useAssignmentUsers() {
  const { data, error, isLoading, mutate } = useSWR(
    ASSIGNMENT_USERS_KEY,
    async () => {
      const result = await getUsersForAssignment();

      if (result?.success) {
        return { users: result.data || [] };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch assignment users');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,   // static-ish list
      dedupingInterval: 60_000,
      errorRetryCount: 2,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  return {
    users: data?.users || [],
    isLoading,
    error,
    mutate,
  };
}

// ─────────────────────────────────────────────
//  Hook: useFollowUpActions
//  Imperative helpers that wrap service calls and
//  automatically invalidate relevant SWR caches.
// ─────────────────────────────────────────────

/**
 * Returns action functions for creating, updating, rescheduling,
 * and deleting follow-ups. Each action invalidates the appropriate
 * SWR cache entries on success.
 *
 * @param {Object} [options]
 * @param {string} [options.leadId]  – when provided, also invalidates the per-lead cache
 * @returns {{
 *   create: function,
 *   update: function,
 *   updateStatus: function,
 *   reschedule: function,
 *   updateMultiple: function,
 *   bulkUpdateStatus: function,
 *   remove: function,
 * }}
 */
export function useFollowUpActions({ leadId } = {}) {
  const invalidate = async (targetLeadId = leadId) => {
    // Always refresh calendar data
    await invalidateCalendarFollowUpsCache();
    // If we know the lead, also refresh its follow-up list, notes, and timeline
    if (targetLeadId) {
      await invalidateLeadFollowUpsCache(targetLeadId);
      await invalidateNotesCache(targetLeadId);
      await invalidateTimelineCache(targetLeadId);
    } else {
      await invalidateNotesCache();
      await invalidateTimelineCache();
    }
  };

  /**
   * Create a new follow-up.
   * @param {Object} followUpData
   */
  const create = async (followUpData) => {
    const result = await createMultipleFollowUp(followUpData);
    const targetId = followUpData?.leadId || followUpData?.LeadId || (Array.isArray(followUpData?.leadIds) ? followUpData.leadIds[0] : null) || leadId;
    if (result?.success) await invalidate(targetId);
    return result;
  };

  /**
   * Update an existing follow-up.
   * @param {string} followUpId
   * @param {Object} followUpData
   */
  const update = async (followUpId, followUpData) => {
    const result = await updateFollowUp(followUpId, followUpData);
    const targetId = followUpData?.leadId || followUpData?.LeadId || leadId;
    if (result?.success) await invalidate(targetId);
    return result;
  };

  /**
   * Update the status / outcome of a single follow-up.
   * @param {string} followUpId
   * @param {Object} statusData
   */
  const updateStatus = async (followUpId, statusData) => {
    const result = await updateFollowUpStatus(followUpId, statusData);
    const targetId = statusData?.leadId || statusData?.LeadId || leadId;
    if (result?.success) await invalidate(targetId);
    return result;
  };

  /**
   * Reschedule an existing follow-up to a new date/time.
   * @param {Object} rescheduleData
   */
  const reschedule = async (rescheduleData) => {
    const result = await rescheduleFollowUp(rescheduleData);
    const targetId = rescheduleData?.leadId || rescheduleData?.LeadId || leadId;
    if (result?.success) await invalidate(targetId);
    return result;
  };

  /**
   * Update multiple follow-ups in one call.
   * @param {string[]} followUpIds
   * @param {Object}   updateData
   */
  const updateMultiple = async (followUpIds, updateData) => {
    const result = await updateMultipleFollowUps(followUpIds, updateData);
    const targetId = updateData?.leadId || updateData?.LeadId || leadId;
    if (result?.success) await invalidate(targetId);
    return result;
  };

  /**
   * Bulk-update status for multiple follow-ups.
   * @param {string[]} followUpIds
   * @param {Object}   statusData
   */
  const bulkUpdateStatus = async (followUpIds, statusData) => {
    const result = await bulkUpdateFollowUpStatus(followUpIds, statusData);
    const targetId = statusData?.leadId || statusData?.LeadId || leadId;
    if (result?.success) await invalidate(targetId);
    return result;
  };

  /**
   * Delete a follow-up.
   * @param {string} followUpId
   */
  const remove = async (followUpId) => {
    const result = await deleteFollowUp(followUpId);
    if (result?.success) await invalidate(leadId);
    return result;
  };

  return {
    create,
    update,
    updateStatus,
    reschedule,
    updateMultiple,
    bulkUpdateStatus,
    remove,
  };
}

// ─────────────────────────────────────────────
//  Hook: useFollowUps
//  General filtered list (getFollowUps service).
// ─────────────────────────────────────────────

/**
 * Fetch a general (optionally filtered) list of follow-ups.
 * Maps to the `getFollowUps` service — supports status, assignedTo, date filters.
 *
 * @param {Object} [filters={}] – e.g. { status, assignedTo, date }
 * @returns {{
 *   followUps: Array,
 *   isLoading: boolean,
 *   error: any,
 *   mutate: function,
 *   refresh: function,
 * }}
 */
export function useFollowUps(filters = {}) {
  const key = ['followUps', 'list', JSON.stringify(filters)];

  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getFollowUps(filters);

      if (result?.success) {
        return { followUps: result.data || [] };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch follow-ups');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      errorRetryCount: 3,
      errorRetryInterval: 5000,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  const refresh = () =>
    globalMutate(
      (k) => Array.isArray(k) && k[0] === 'followUps' && k[1] === 'list',
      undefined,
      { revalidate: true }
    );

  return {
    followUps: data?.followUps || [],
    isLoading,
    error,
    mutate,
    refresh,
  };
}

// ─────────────────────────────────────────────
//  Hook: useUpcomingFollowUps
//  Fetches upcoming follow-ups for the next N days.
// ─────────────────────────────────────────────

/**
 * Fetch upcoming follow-ups for the next `days` days (dashboard widget).
 * Maps to the `getUpcomingFollowUps` service.
 *
 * @param {number} [days=7]
 * @returns {{
 *   followUps: Array,
 *   isLoading: boolean,
 *   error: any,
 *   mutate: function,
 *   refresh: function,
 * }}
 */
export function useUpcomingFollowUps(days = 7) {
  const key = ['followUps', 'upcoming', days];

  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getUpcomingFollowUps(days);

      if (result?.success) {
        return { followUps: result.data || [] };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch upcoming follow-ups');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 5000,
      errorRetryCount: 2,
      errorRetryInterval: 5000,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  const refresh = () =>
    globalMutate(
      (k) => Array.isArray(k) && k[0] === 'followUps' && k[1] === 'upcoming',
      undefined,
      { revalidate: true }
    );

  return {
    followUps: data?.followUps || [],
    isLoading,
    error,
    mutate,
    refresh,
  };
}

// ─────────────────────────────────────────────
//  Hook: useFollowUpStats
//  Fetches aggregate follow-up statistics.
// ─────────────────────────────────────────────

/**
 * Fetch aggregate follow-up statistics (total, scheduled, completed, overdue, today).
 * Maps to the `getFollowUpStats` service — useful for dashboard summary cards.
 *
 * @returns {{
 *   stats: { total, scheduled, completed, overdue, today } | null,
 *   isLoading: boolean,
 *   error: any,
 *   mutate: function,
 *   refresh: function,
 * }}
 */
export function useFollowUpStats() {
  const key = ['followUps', 'stats'];

  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getFollowUpStats();

      if (result?.success) {
        return { stats: result.data || null };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch follow-up statistics');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 10_000,
      errorRetryCount: 2,
      errorRetryInterval: 5000,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  const refresh = () =>
    globalMutate(
      (k) => Array.isArray(k) && k[0] === 'followUps' && k[1] === 'stats',
      undefined,
      { revalidate: true }
    );

  return {
    stats: data?.stats || null,
    isLoading,
    error,
    mutate,
    refresh,
  };
}

// ─────────────────────────────────────────────
//  Hook: useMonthlyFollowUps
//  Legacy month + year calendar view.
// ─────────────────────────────────────────────

/**
 * Fetch follow-up calendar data for a specific month and year.
 * Maps to the `getFollowUpsByMonthAndYear` service (legacy endpoint).
 * Prefer `useCalendarFollowUps` for new code; use this only when the
 * legacy `/GetFollowupsBymonthAndyear` endpoint is explicitly required.
 *
 * @param {number|null} month – 1-based (1 = January … 12 = December)
 * @param {number|null} year
 * @returns {{
 *   calendarData: Object,   // keyed by YYYY-MM-DD
 *   rawData: Array,
 *   totalRecords: number,
 *   isLoading: boolean,
 *   error: any,
 *   mutate: function,
 *   refresh: function,
 * }}
 */
export function useMonthlyFollowUps(month, year) {
  const key = month && year ? ['followUps', 'monthly', month, year] : null;

  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getFollowUpsByMonthAndYear(month, year);

      if (result?.success) {
        return {
          calendarData: result.data || {},
          rawData: result.rawData || [],
          totalRecords: result.totalRecords || 0,
        };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch monthly follow-ups');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      errorRetryCount: 2,
      errorRetryInterval: 5000,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  const refresh = () =>
    globalMutate(
      (k) => Array.isArray(k) && k[0] === 'followUps' && k[1] === 'monthly',
      undefined,
      { revalidate: true }
    );

  return {
    calendarData: data?.calendarData || {},
    rawData: data?.rawData || [],
    totalRecords: data?.totalRecords ?? 0,
    isLoading,
    error,
    mutate,
    refresh,
  };
}

// ─────────────────────────────────────────────
//  Hook: useLeadFollowUpsMock
//  Dev-only mock version of per-lead follow-ups.
// ─────────────────────────────────────────────

/**
 * Fetch per-lead follow-ups using the `getLeadFollowUps` service.
 * This service uses **mock data in development** and a different API
 * path in production. Use `useLeadFollowUps` (backed by `getFollowUpsByLeadId`)
 * for the real production endpoint.
 *
 * @param {string|null} leadId
 * @returns {{
 *   followUps: Array,
 *   isLoading: boolean,
 *   error: any,
 *   mutate: function,
 *   refresh: function,
 * }}
 */
export function useLeadFollowUpsMock(leadId) {
  const key = leadId ? ['followUps', 'byLeadMock', String(leadId)] : null;

  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getLeadFollowUps(leadId);

      if (result?.success) {
        return { followUps: result.data || [] };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch lead follow-ups');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      errorRetryCount: 3,
      errorRetryInterval: 5000,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  const refresh = () =>
    globalMutate(
      (k) => Array.isArray(k) && k[0] === 'followUps' && k[1] === 'byLeadMock',
      undefined,
      { revalidate: true }
    );

  return {
    followUps: data?.followUps || [],
    isLoading,
    error,
    mutate,
    refresh,
  };
}

// ──────────────────────────────────────────────
//  Hook: useFollowUpsList
//  Paginated list for the ActivitiesList table view (POST payload).
// ──────────────────────────────────────────────

/**
 * Fetch a paginated list of follow-ups for the ActivitiesList table view.
 * Maps to POST /api/FollowUp/GetFollowUps with body { type, pageNumber, pageSize }.
 *
 * @param {Object} [params={}]
 * @param {number} [params.type=0]        – 0=All, 1=Leads, 2=Applicants
 * @param {number} [params.pageNumber=1]  – current page (1-based)
 * @param {number} [params.pageSize=50]   – records per page (0 = all)
 * @returns {{
 *   activities: Array,
 *   totalRecords: number,
 *   isLoading: boolean,
 *   error: any,
 *   mutate: function,
 *   refresh: function,
 * }}
 */
export function useFollowUpsList({
  type = 0,
  pageNumber = 1,
  pageSize = 50,
  search = "",
  statusFilter = [],
  assignToFilter = [],
  activityTypeFilter = [],
  shouldFetch = true,
  sortBy = "",
  sortOrder = "",
} = {}) {
  const trimmedSearch = (search || "").trim();
  const activeSearch = trimmedSearch.length >= 3 ? trimmedSearch : "";
  const key = shouldFetch ? ['followUps', 'listView', type, pageNumber, pageSize, activeSearch, statusFilter, assignToFilter, activityTypeFilter, sortBy, sortOrder] : null;

  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getFollowUpsList({ 
        type, 
        pageNumber, 
        pageSize, 
        search: activeSearch,
        statusFilter,
        assignToFilter,
        activityTypeFilter,
        sortBy,
        sortOrder
      });

      if (result?.success) {
        return {
          activities: result.data || [],
          totalRecords: result.totalRecords ?? 0,
        };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch activities list');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 3000,
      errorRetryCount: 3,
      errorRetryInterval: 5000,
      refreshWhenOffline: false,
      suspense: false,
      keepPreviousData: true,
    }
  );

  /** Invalidate all listView pages for the current type. */
  const refresh = () =>
    globalMutate(
      (k) =>
        Array.isArray(k) &&
        k[0] === 'followUps' &&
        k[1] === 'listView' &&
        k[2] === type,
      undefined,
      { revalidate: true }
    );

  return {
    activities: data?.activities || [],
    totalRecords: data?.totalRecords ?? 0,
    isLoading,
    error,
    mutate,
    refresh,
  };
}

export function useActivityById(followUpId) {
  const key = followUpId ? ['followUps', 'activityDetail', String(followUpId)] : null;

  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getActivityById(followUpId);

      if (result?.success) {
        return { activity: result.data || null };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch activity details');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 5000,
      errorRetryCount: 2,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  const refresh = () =>
    globalMutate(
      (k) => Array.isArray(k) && k[0] === 'followUps' && k[1] === 'activityDetail' && k[2] === String(followUpId),
      undefined,
      { revalidate: true }
    );

  return {
    activity: data?.activity || null,
    isLoading,
    error,
    mutate,
    refresh,
  };
}

export function useFollowUpBulkActions() {
  const { cache } = useSWRConfig();

  const handleBulkAction = async (payload) => {
    try {
      const { bulkFollowUpActionDispatcher } = await import('services/followUpService');
      const response = await bulkFollowUpActionDispatcher(payload);
      
      if (response?.success) {
        // Invalidate all followUp caches to ensure fresh data
        if (cache instanceof Map) {
          for (const key of cache.keys()) {
            if (Array.isArray(key) && key[0] === 'followUps') {
              globalMutate(key, undefined, { revalidate: true });
            }
          }
        } else {
          // Fallback invalidation
          globalMutate(
            (k) => Array.isArray(k) && k[0] === 'followUps',
            undefined,
            { revalidate: true }
          );
        }
        await invalidateMyScheduledItemsCache();
      }
      return response;
    } catch (err) {
      return { success: false, error: err.message || "Failed to perform bulk action" };
    }
  };

  return { handleBulkAction };
}
